import { RetryConfig } from "./NetworkUtils";
import {
  UploadRequest,
  StartFileUpload,
  UploadBlock,
  CommitBlocks,
} from "./UploadApi";

export type UploadSettings = {
  uploadUrl?: string;
  progressCallback?: (progress: number) => void;
  channelName: string;
  retryConfig?: RetryConfig;
};

const apiBase = (() => {
  const script = document.currentScript;
  
  if (script?.dataset.apiBase) {
    return script.dataset.apiBase;
  }

  if (script instanceof HTMLScriptElement && script.src) {
    return new URL("/", script.src).toString();
  }

  return null;
})();

export class UploadClient {
  baseUrl: string;
  progressCallback?: (progress: number) => void;
  retryConfig?: RetryConfig;

  constructor({ uploadUrl, progressCallback, channelName, retryConfig }: UploadSettings) {
    if (!channelName) {
      throw new Error("channelName is required in UploadSettings");
    }

    const apiBaseUrl =
      uploadUrl ??
      apiBase ??
      (() => {
        throw new Error(
          "Unable to determine API base URL. Please provide uploadUrl in UploadSettings or ensure the script tag has a data-api-base attribute.",
        );
      })();

    this.baseUrl = `${apiBaseUrl.endsWith("/") ? apiBaseUrl : apiBaseUrl + "/"}api/channels/${channelName}`;
    this.progressCallback = progressCallback;
    this.retryConfig = retryConfig;
  }

  async uploadFile(
    file: File,
    metadata: Record<string, string>,
    uploadId?: string,
  ): Promise<void> {
    const request: UploadRequest = {
      uploadId: uploadId || crypto.randomUUID(),
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      metadata: metadata,
    };

    const session = await StartFileUpload(this.baseUrl, request, this.retryConfig);

    const chunkSize = 1 * 1024 * 1024;
    let offset = 0;
    while (offset < file.size) {
      const chunk = file.slice(offset, offset + chunkSize);
      await UploadBlock(session.fileUrl, offset, chunk, this.retryConfig);
      offset += chunkSize;
      this.updateProgress(Math.min(offset, file.size), file.size);
    }

    await CommitBlocks(this.baseUrl, session.fileId, this.retryConfig);
  }

  private updateProgress(loaded: number, total: number) {
    const progress = Math.round((loaded / total) * 100);
    if (this.progressCallback) {
      this.progressCallback(progress);
    }
  }
}
